import MomentUtils from '@date-io/moment';
import Framework, { LoadingHandler, StateErrorDisplay, shapes } from '@greenville/framework';
import { Box, Button, Grid, Paper, Typography } from '@material-ui/core';
import { ThemeProvider, createTheme, withStyles } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import _ from 'lodash';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import Loader from '../../../../common/Loader';
import ChatgptConfig from '../../../../common/config/ChatgptConfig';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import AutocompleteComponent from '../../common/components/Autocomplete';
import AIChartsUtilityModel from '../../models/AIChartsUtility';
import AIContentFilterChartsModel from '../../models/AIContentFilterCharts';
import AIContentFilterChartsDetailsModel from '../../models/AIContentFilterChartsDetails';
import AIFeedbackChartsDetailsModel from '../../models/AIFeedbackChartsDetails';
import ChannelsListing from '../../models/ChannelsListing';
import ChannelsTrendsModel from '../../models/ChannelsTrends';
import ChatgptAdmin from '../../models/ChatgptAdmin';
import TenantConfigModel from '../../models/TenantConfig';
import AIChartsComponent from '../aiCharts/AIChartsComponent';
import AIContentFilterCharts from '../aiCharts/contentFilterCharts/AIContentFilterCharts';
import ChannelsTrendsGrid from './datagrid/ChannelsTrendsGrid';

const materialTheme = createTheme({
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: constants.PEARSON_PRIMARY_COLOR
      }
    },
    MuiPickersYear: {
      root: {
        '&:hover': {
          backgroundColor: '#d2cfc4'
        }
      },
      yearSelected: {
        color: constants.PEARSON_PRIMARY_COLOR
      },
      current: {
        color: constants.PEARSON_PRIMARY_COLOR
      }
    },
    MuiPickersDay: {
      day: {
        '&:hover': {
          backgroundColor: '#d2cfc4'
        }
      },
      daySelected: {
        backgroundColor: constants.PEARSON_PRIMARY_COLOR,
        '&:hover': {
          backgroundColor: constants.PEARSON_PRIMARY_COLOR
        }
      },
      current: {
        color: constants.PEARSON_PRIMARY_COLOR
      }
    }
  }
});

const themes = createTheme({
  palette: {
    primary: {
      light: '#047a9c',
      main: '#005d83',
      dark: '#003558',
      contrastText: '#ffffff'
    }
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: 30
      }
    }
  }
});

const styles = (theme) => ({
  buttonGroupStyle: {
    justifyContent: 'center'
  },
  datePicker: {
    marginTop: 'inherit'
  },
  marginBox: {
    border: '1px solid #322c2c2e',
    width: '52%',
    padding: '2px 15px 2px 0px',
    borderRadius: '4px',
    color: '#fff',
    height: '41px'
  },
  blurStyle: {
    position: 'absolute',
    background: 'rgb(255 255 255 / 20%)',
    top: '0',
    width: '93%',
    height: '100%',
    backdropFilter: 'blur(0.3px)',
    zIndex: '9999'
  },
  buttonStyle: {
    width: '120px',
    margin: '10px'
  },
  container: {
    marginBottom: 20
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'left',
    color: theme.palette.text.secondary
  },
  title: {
    marginTop: -20
  },
  labelWidht: {
    width: '380px'
  },
  boxWidth: {
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(4)
  },
  button: {
    paddingTop: theme.spacing(2),
    marginBottom: '-3em'
  },
  root: {
    top: '12%'
  },
  msgStyle: {
    fontSize: 'large',
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(0)
    },
    zIndex: 9999
  }
});

@inject(
  'channelsListingUtility',
  'ChannelsUtilityTrendsListingStatus',
  'channelsTrendsUtility',
  'channelsUtilityTrendsStatus',
  'chatgptAdmin',
  'ChatGptUtilityGetAdminStatus',
  'internalUsers',
  'tenantConfig',
  'aiChartsUtility',
  'aiContentfilterCharts',
  'aiContentfilterChartsDetails',
  'aiFeedbackChartsDetails',
  'AIChartsUtilityService',
  'AIContentFilterChartsService',
  'aiFeedbackChartsDetailsListService',
  'AIContentFilterChartsDetailsService'
)
@observer
class ChannelsTrends extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    channelsTrendsUtility: shapes.modelOf(ChannelsTrendsModel).isRequired,
    channelsListingUtility: shapes.modelOf(ChannelsListing).isRequired,
    ChannelsUtilityTrendsListingStatus: shapes.state.isRequired,
    channelsUtilityTrendsStatus: shapes.state.isRequired,
    ChatGptUtilityGetAdminStatus: shapes.state.isRequired,
    chatgptAdmin: shapes.modelOf(ChatgptAdmin).isRequired,
    tenantConfig: shapes.modelOf(TenantConfigModel).isRequired,
    history: PropTypes.object.isRequired,
    aiChartsUtility: shapes.modelOf(AIChartsUtilityModel).isRequired,
    aiContentfilterCharts: shapes.modelOf(AIContentFilterChartsModel).isRequired,
    aiContentfilterChartsDetails: shapes.modelOf(AIContentFilterChartsDetailsModel).isRequired,
    aiFeedbackChartsDetails: shapes.modelOf(AIFeedbackChartsDetailsModel).isRequired,
    AIChartsUtilityService: shapes.modelOf(AIChartsUtilityModel).isRequired,
    AIContentFilterChartsService: shapes.modelOf(AIContentFilterChartsModel).isRequired,
    aiFeedbackChartsDetailsListService: shapes.modelOf(AIFeedbackChartsDetailsModel).isRequired,
    AIContentFilterChartsDetailsService: shapes.modelOf(AIContentFilterChartsDetailsModel).isRequired
  };

  constructor(props) {
    super(props);
    const { channelsTrendsUtility, channelsListingUtility, ChannelsUtilityTrendsListingStatus } = props;
    this.defaultSearch = {
      channelsId: '',
      startDate: '',
      endDate: ''
    };
    this.state = {
      defaultDropdownValue: { title: 'general-chemistry', channelsId: 'general-chemistry', id: 1 },
      newRequest: true,
      searchFields: {
        channelsId: 'general-chemistry'
      },
      gptResponse: channelsTrendsUtility,
      gptListingResponse: {
        chatListDatas: channelsListingUtility,
        chatListStatus: ChannelsUtilityTrendsListingStatus,
        loaderStatusResponse: true
      },
      endDisabled: true,
      showDateFields: false,
      showOutline: constants.CLUSTER_ONE_DAY_VALUE,
      setOrder: 'ASC',
      toggleBtnValue: 'cluster',
      selectListingOption: false,
      loaderStatus: true,
      setDirectionValue: '',
      selectRadioOption: false,
      channelsList: [],
      inputValue: '',
      isUserIdExcluded: true,
      tokenValue: '',
      showCharts: {
        userFeedBackChart: false,
        blockedCountChart: false
      },
      isSearchClicked: false,
      tenantName: '',
      startDateError: false,
      endDateError: false
    };
  }

  setTenantDetails = () => {
    const { history } = this.props;
    const selectedTabName = history.location.pathname.split('/');
    ChatgptConfig.tenantData.forEach((item) => {
      if (item.name === selectedTabName[2]) {
        this.setState({
          tenantName: item.name
        });
      }
    });
  };

  componentDidMount = () => {
    this.setTenantDetails();
    const { channelsTrendsUtility, chatgptAdmin } = this.props;
    /* eslint-disable camelcase */
    const temp = window.location.pathname;
    if (temp === '/chatutility/channels/TrendsInsights') {
      this.setState({
        selectRadioOption: false,
        selectListingOption: false
      });
    }

    chatgptAdmin.fetchTenantData('channels');
    /* eslint-disable camelcase */
    Framework.getEventManager().on(constants.SET_ADMIN_DATA, () => {
      const { groups } = chatgptAdmin;
      if (groups && groups.length > 0) {
        const channelsNames = groups.filter((item) => item.name === 'channelsName')[0];
        this.setState({ channelsList: channelsNames });
      }
    });

    Framework.getEventManager().on(constants.GET_CHANNELS_LISTING_MODEL_DATA, () => {
      const { channelsListingUtility } = this.props;
      const { gptListingResponse } = this.state;
      this.setState({
        gptListingResponse: {
          ...gptListingResponse,
          chatListDatas: channelsListingUtility
        }
      });
    });

    Framework.getEventManager().on(constants.GET_CHANNELS_TRENDS_MODEL_DATA, () => {
      const { data } = channelsTrendsUtility;
      if (channelsTrendsUtility) {
        const { newRequest, gptResponse } = this.state;
        const combineData = [...data];
        ChatgptConfig.channelsTypeData.forEach((value) => {
          const alteredValue = value.name;
          if (!alteredValue) return;
          combineData.forEach((dataValue, index) => {
            if (dataValue.type === alteredValue) {
              combineData[index].title = value.label;
            }
          });
        });
        const combineDatatoJS = JSON.parse(JSON.stringify(toJS(combineData)));
        /* eslint-enable camelcase */
        if (newRequest) {
          const sortedDate = combineDatatoJS.sort((x, y) => new Date(y.date) - new Date(x.date));
          this.setState({ gptResponse: sortedDate });
        } else {
          const combineDataGpt = [...gptResponse, ...combineDatatoJS];
          const sortedDate = combineDataGpt.sort((x, y) => new Date(y.date) - new Date(x.date));
          this.setState((prev) => ({ gptResponse: [...prev.gptResponse, ...sortedDate] }));
        }
      }
    });
  };

  handleChange = (e) => {
    const { searchFields } = this.state;
    const fieldChanged = {};
    fieldChanged[e.target.name] = e.target.value;
    this.setState({
      searchFields: { ...searchFields, ...fieldChanged }
    });
  };

  handleToggleBtnChange = (e) => {
    const {
      channelsListingUtility,
      channelsTrendsUtility,
      aiChartsUtility,
      aiFeedbackChartsDetails,
      aiContentfilterCharts,
      aiContentfilterChartsDetails
    } = this.props;
    this.setState({
      toggleBtnValue: e.target.value,
      selectRadioOption: e.target.value
    });
    channelsListingUtility.resetStoreValues();
    channelsTrendsUtility.resetStoreValues();
    aiChartsUtility.resetStoreValues();
    aiFeedbackChartsDetails.resetStoreValues();
    aiContentfilterCharts.resetStoreValues();
    aiContentfilterChartsDetails.resetStoreValues();
  };

  handleReset = () => {
    const { gptListingResponse } = this.state;
    const { aiChartsUtility, aiFeedbackChartsDetails, aiContentfilterCharts, aiContentfilterChartsDetails } =
      this.props;
    this.setState({
      inputValue: constants.DEFAULT_CHANNELS_ID,
      showOutline: constants.CLUSTER_ONE_DAY_VALUE,
      showDateFields: false,
      toggleBtnValue: 'cluster',
      searchFields: {
        ...this.defaultSearch,
        channelsId: constants.DEFAULT_CHANNELS_ID
      },
      gptListingResponse: {
        ...gptListingResponse,
        chatListDatas: []
      }
    });
    aiChartsUtility.resetStoreValues();
    aiFeedbackChartsDetails.resetStoreValues();
    aiContentfilterCharts.resetStoreValues();
    aiContentfilterChartsDetails.resetStoreValues();
  };

  handleSearch = (customPayload) => {
    const { searchFields, toggleBtnValue, startDateError, endDateError, showOutline } = this.state;
    const { channelsListingUtility, channelsTrendsUtility, tenantConfig } = this.props;
    const tenantId = utils.getTenantId('channels', tenantConfig);
    if (startDateError || endDateError) {
      return;
    }
    if (customPayload && customPayload.lastKey) {
      this.setState({ newRequest: false });
    } else {
      this.setState({ newRequest: true });
    }
    const payload = { ...searchFields, ...customPayload };

    let endDateChanged = new Date();
    endDateChanged = moment(endDateChanged).format(constants.YYYY_MM_DD_FORMAT);

    if ((payload && payload.startDate) || (payload && payload.channelsId)) {
      if (!payload.endDate && payload.startDate) {
        payload.endDate = endDateChanged;
        this.setState({
          searchFields: { ...searchFields, endDate: endDateChanged }
        });
      }
      if (!payload.endDate && !payload.startDate) {
        payload.endDate = endDateChanged;
        payload.startDate = endDateChanged;
        this.setState({
          searchFields: { ...searchFields, startDate: endDateChanged, endDate: endDateChanged }
        });
      }
    } else {
      payload.endDate = endDateChanged;
      payload.startDate = endDateChanged;
      this.setState({
        searchFields: { ...searchFields, startDate: endDateChanged, endDate: endDateChanged }
      });
    }
    if (toggleBtnValue === 'cluster') {
      this.setState({
        selectRadioOption: false,
        selectListingOption: true
      });
      delete payload.channelsId;
      if (showOutline === constants.CLUSTER_ONE_DAY_VALUE) {
        payload.startDate = endDateChanged;
      }
      if (_.isEmpty(searchFields.channelsId)) {
        payload.identifierId = 'general-chemistry';
      } else {
        payload.identifierId = searchFields.channelsId;
      }
      channelsListingUtility.fetch({ ...payload, tenantId });
    } else if (toggleBtnValue === 'count') {
      payload.bookId = payload.channelsId;
      delete payload.channelsId;
      channelsTrendsUtility.fetch(payload, tenantId);
    }

    if (toggleBtnValue === 'userFeedBack') {
      this.setState({
        showCharts: {
          userFeedBackChart: true,
          blockedCountChart: false
        },
        isSearchClicked: true
      });
    }
    if (toggleBtnValue === 'blockedCount') {
      this.setState({
        showCharts: {
          userFeedBackChart: false,
          blockedCountChart: true
        },
        isSearchClicked: true
      });
    }
  };

  trendsDataFetch = (value) => {
    const { showDateFields } = this.state;
    if (value === 'custom') {
      this.setState({
        showOutline: value,
        showDateFields: !showDateFields
      });
    }
  };

  handleDateChange = (date, name) => {
    this.setState((prevState) => {
      const { searchFields } = prevState;
      const fieldChanged = {};
      fieldChanged[name] = moment(date).format(constants.MM_DD_YYYY_FORMAT);

      const newState = {
        [`${name}Error`]: false,
        searchFields: { ...searchFields },
        endDisabled: false
      };

      if (date === null && fieldChanged[name] === 'Invalid date') {
        newState.searchFields[name] = '';
      } else if (fieldChanged[name] === 'Invalid date') {
        newState[`${name}Error`] = true;
        /* eslint-disable-next-line no-underscore-dangle */
        newState.searchFields[name] = date._i;
      } else {
        newState.searchFields[name] = fieldChanged[name];
      }

      if (name === 'startDate' && fieldChanged[name] === 'Invalid date') {
        newState.endDisabled = true;
        newState.searchFields.endDate = '';
        newState.endDateError = false;
      }

      return newState;
    });
  };

  handleDateclicker = (event, value) => {
    const { searchFields } = this.state;
    const daysvalue = event.target.innerText;
    let startDate = new Date();
    let endDate = new Date();
    switch (daysvalue) {
      case '1d':
        this.setState({
          showOutline: value,
          showDateFields: false
        });
        break;
      case '3d':
        startDate.setDate(startDate.getDate() - 2);
        this.setState({
          showOutline: value,
          showDateFields: false
        });
        break;
      case '1w':
        startDate.setDate(startDate.getDate() - 6);
        this.setState({
          showOutline: value,
          showDateFields: false
        });
        break;
      default:
    }
    startDate = moment(startDate).format(constants.YYYY_MM_DD_FORMAT);
    endDate = moment(endDate).format(constants.YYYY_MM_DD_FORMAT);
    this.setState({
      searchFields: { ...searchFields, startDate, endDate }
    });
  };

  handleAscending = (dir, value) => {
    const { gptResponse } = this.state;
    if (dir === 'asc') {
      const sorted = [...gptResponse].sort((a, b) => (a[value] > b[value] ? 1 : -1));
      this.setState({
        ...gptResponse,
        gptResponse: sorted
      });
    } else {
      const sorted = [...gptResponse].sort((a, b) => (a[value] < b[value] ? 1 : -1));
      this.setState({
        ...gptResponse,
        gptResponse: sorted
      });
    }
  };

  sorting = (value) => {
    const { setOrder } = this.state;
    if (setOrder === 'ASC') {
      this.handleAscending('asc', value);
      this.setState({
        setOrder: 'DSC',
        setDirectionValue: value
      });
    }
    if (setOrder === 'DSC') {
      this.handleAscending('des', value);
      this.setState({
        setOrder: 'ASC',
        setDirectionValue: value
      });
    }
  };

  onAutoCompleteChange = (event, newValue) => {
    const { searchFields } = this.state;
    this.setState({
      searchFields: {
        ...searchFields,
        channelsId: newValue !== null ? newValue.id : ''
      }
    });
  };

  onInputChange = (event, newInputValue) => {
    this.setState({
      inputValue: newInputValue
    });
  };

  updateChannelCountData = (data) => {
    const updatedData =
      data.length > 0 &&
      data?.map(({ bookId, ...item }) => ({
        ...item,
        channelsId: bookId // Rename bookId to channelId
      }));
    return updatedData;
  };

  handleOnSearch = (param) => {
    this.setState({
      isSearchClicked: param
    });
  };

  formatSearchFields = (searchData) => {
    const data = {
      ...searchData,
      bookId: searchData.channelsId
    };
    delete data.channelsId;
    return data;
  };

  render() {
    const {
      classes,
      channelsUtilityTrendsStatus,
      ChatGptUtilityGetAdminStatus,
      tenantConfig,
      AIChartsUtilityService,
      AIContentFilterChartsService,
      aiFeedbackChartsDetailsListService,
      AIContentFilterChartsDetailsService
    } = this.props;
    const {
      searchFields,
      endDisabled,
      showDateFields,
      showOutline,
      toggleBtnValue,
      gptResponse,
      gptListingResponse,
      setOrder,
      setDirectionValue,
      selectRadioOption,
      selectListingOption,
      loaderStatus,
      inputValue,
      channelsList,
      defaultDropdownValue,
      isUserIdExcluded,
      tokenValue,
      isSearchClicked,
      showCharts,
      tenantName,
      startDateError,
      endDateError
    } = this.state;
    const { chatListStatus } = gptListingResponse;
    const activeSession = utils.getSessionData();
    if (activeSession !== null && tokenValue === '') {
      const token = utils.getToken();
      this.setState({
        tokenValue: token
      });
    }

    return (
      <>
        {ChatGptUtilityGetAdminStatus.isPending ? (
          <Loader />
        ) : (
          <>
            <Paper>
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h5" align="center" gutterBottom spacing={9}>
                    {constants.CHAT_CRITERIA_TEXT}
                  </Typography>
                </Box>
                <Grid container direction="row">
                  <Grid item xs={2} />
                  <Grid item xs={8}>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', p: '0px 33px' }}>
                      {/* <FormControl sx={{ minWidth: '45%', maxWidth: '90%', mr: 4 }} size="small">
                    <TextField
                      label="Channels Id"
                      margin="dense"
                      name="channelsId"
                      onChange={($event) => this.handleChange($event)}
                      style={{ minWidth: '32%', marginTop: '0px' }}
                      value={searchFields.channelsId}
                      variant="outlined"
                    />
                  </FormControl> */}
                      {channelsList && channelsList.properties && (
                        <FormControl sx={{ minWidth: '45%', maxWidth: '90%', mr: 4 }} size="small">
                          <AutocompleteComponent
                            options={channelsList.properties}
                            onAutoCompleteChange={this.onAutoCompleteChange}
                            onInputChange={this.onInputChange}
                            stateVal={inputValue}
                            errorVal=""
                            name="channelsId"
                            label="Channels Id"
                            componentDefaultValue={defaultDropdownValue}
                          />
                        </FormControl>
                      )}
                      <Box className={classes.marginBox} sx={{ '& button': { m: 31, p: 12 } }}>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant={showOutline === constants.CLUSTER_ONE_DAY_VALUE ? 'outlined' : 'text'}
                            style={{ color: '#0d0c0c8c' }}
                            onClick={(event) => this.handleDateclicker(event, constants.CLUSTER_ONE_DAY_VALUE)}
                          >
                            1d
                          </Button>
                          <Button
                            size="small"
                            style={{ color: '#0d0c0c8c' }}
                            variant={showOutline === '3D' ? 'outlined' : 'text'}
                            onClick={(event) => this.handleDateclicker(event, '3D')}
                          >
                            3d
                          </Button>
                          <Button
                            size="small"
                            style={{ color: '#0d0c0c8c' }}
                            variant={showOutline === '1W' ? 'outlined' : 'text'}
                            onClick={(event) => this.handleDateclicker(event, '1W')}
                          >
                            1w
                          </Button>
                          <Button
                            size="small"
                            variant={showOutline === 'custom' ? 'outlined' : 'text'}
                            style={{ color: '#0d0c0c8c' }}
                            onClick={() => this.trendsDataFetch('custom')}
                          >
                            custom
                            <CalendarMonthIcon />
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={2} />
                </Grid>
                <br />
                {showDateFields && (
                  <Grid container direction="row">
                    <Grid item xs={2} />
                    <Grid item xs={8}>
                      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', p: '0px 33px' }}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <ThemeProvider theme={materialTheme}>
                            <KeyboardDatePicker
                              className={classes.datePicker}
                              format={constants.MM_DD_YYYY_FORMAT}
                              autoOk
                              size="small"
                              variant="inline"
                              inputVariant="outlined"
                              style={{ minWidth: '32%', marginRight: '6%' }}
                              disableFuture
                              value={searchFields.startDate || null}
                              label="Start Date"
                              onChange={(date) => this.handleDateChange(date, 'startDate')}
                              KeyboardButtonProps={{
                                'aria-label': 'change date'
                              }}
                              helperText={startDateError ? 'Invalid Date Format' : ''}
                              error={startDateError}
                            />
                            <KeyboardDatePicker
                              className={classes.datePicker}
                              // margin="normal"
                              format={constants.MM_DD_YYYY_FORMAT}
                              autoOk
                              variant="inline"
                              size="small"
                              style={{ minWidth: '32%' }}
                              inputVariant="outlined"
                              disableFuture
                              value={searchFields.endDate || null}
                              label="End Date"
                              minDate={searchFields.startDate || undefined}
                              onChange={(date) => this.handleDateChange(date, 'endDate')}
                              disabled={endDisabled}
                              KeyboardButtonProps={{
                                'aria-label': 'change date'
                              }}
                              helperText={endDateError ? 'Invalid Date Format' : ''}
                              error={endDateError}
                            />
                          </ThemeProvider>
                        </MuiPickersUtilsProvider>
                      </Box>
                      <br />
                    </Grid>
                    <Grid item xs={2} />
                  </Grid>
                )}
                <Grid container direction="row">
                  <Grid item xs={8} style={{ marginLeft: '6%' }}>
                    <Box
                      sx={{ pl: '10%', ml: '20%' }}
                      style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}
                    >
                      <Typography style={{ paddingBottom: '10px', fontWeight: '600', fontSize: '15px' }}>
                        Report By:
                      </Typography>
                      <ToggleButtonGroup
                        color="primary"
                        value={toggleBtnValue}
                        exclusive
                        onChange={this.handleToggleBtnChange}
                        aria-label="Platform"
                      >
                        <ToggleButton value="cluster">{constants.CLUSTERING}</ToggleButton>
                        <ToggleButton value="count">{constants.COUNT}</ToggleButton>
                        <ToggleButton value="userFeedBack">User Feedback Chart</ToggleButton>
                        <ToggleButton value="blockedCount">Blocked Count Chart</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Grid>
                  <Grid item xs={6} />
                </Grid>
                <br />
              </Box>
              <br />
              <Box>
                <Grid container justify="space-evenly" className={classes.buttonGroupStyle}>
                  <ThemeProvider theme={themes}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={() => this.handleSearch()}
                      className={classes.buttonStyle}
                    >
                      {constants.CHAT_SEARCH}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={this.handleReset}
                      className={classes.buttonStyle}
                    >
                      {constants.CHAT_RESET}
                    </Button>
                  </ThemeProvider>
                </Grid>
                <br />
              </Box>
            </Paper>
            <LoadingHandler
              loading={
                channelsUtilityTrendsStatus.isPending ||
                chatListStatus.isPending ||
                AIChartsUtilityService.isPending ||
                AIContentFilterChartsService.isPending ||
                aiFeedbackChartsDetailsListService.isPending ||
                AIContentFilterChartsDetailsService.isPending
              }
              isError={
                (channelsUtilityTrendsStatus.isError || chatListStatus.isError) &&
                (channelsUtilityTrendsStatus.error || chatListStatus.isError) &&
                (channelsUtilityTrendsStatus.error === 401 || chatListStatus.error === 401) &&
                (channelsUtilityTrendsStatus.error === 500 || chatListStatus.error === 500)
              }
              content={
                (gptResponse && gptResponse.length > 0) ||
                (gptListingResponse.chatListDatas.data && gptListingResponse.chatListDatas.data.length > 0) ? (
                  <Box>
                    <br />
                    <ChannelsTrendsGrid
                      channelsListingUtilityData={gptListingResponse}
                      selectListingOption={selectListingOption}
                      channelsUtilityTrendsData={toJS(this.updateChannelCountData(gptResponse))}
                      sorting={this.sorting}
                      setOrder={setOrder}
                      setDirectionValue={setDirectionValue}
                      selectRadioOption={selectRadioOption}
                      startDate={searchFields.startDate || ''}
                      endDate={searchFields.endDate || ''}
                      tenantId={utils.getTenantId('channels', tenantConfig)}
                      channelsList={channelsList.properties || []}
                    />
                  </Box>
                ) : (
                  <>
                    <br />
                    {['count', 'cluster'].includes(toggleBtnValue) ? (
                      <Paper>
                        <br />
                        <Box>
                          <br />
                          <Typography
                            variant="h4"
                            align="center"
                            gutterBottom
                            spacing={9}
                            className={classes.title}
                            style={{ p: 5, mt: 25 }}
                          >
                            {constants.CHAT_FETCH}
                          </Typography>
                        </Box>
                      </Paper>
                    ) : (
                      <></>
                    )}
                  </>
                )
              }
              loadingContent={<Loader loaderStatus={loaderStatus} />}
              errorContent={
                <StateErrorDisplay error={channelsUtilityTrendsStatus.error || chatListStatus.error} showDetails />
              }
            />
            {showCharts.userFeedBackChart && (
              <AIChartsComponent
                searchFields={this.formatSearchFields(searchFields)}
                handleOnSearch={this.handleOnSearch}
                isSearchClicked={isSearchClicked}
                isUserIdExcluded={isUserIdExcluded}
                tenantName={tenantName}
              />
            )}
            {showCharts.blockedCountChart && (
              <AIContentFilterCharts
                searchFields={this.formatSearchFields(searchFields)}
                handleOnSearch={this.handleOnSearch}
                isSearchClicked={isSearchClicked}
                bookList={channelsList?.properties}
                isUserIdExcluded={isUserIdExcluded}
                tenantName={tenantName}
              />
            )}
          </>
        )}
      </>
    );
  }
}

export default withStyles(styles)(ChannelsTrends);
